import { ChevronLeft, ChevronRight } from 'lucide-react'
import { DayPicker, type DayPickerProps } from 'react-day-picker'

import { cn } from '@/lib/utils'
import { buttonVariants } from '@/components/ui/button'

export type CalendarProps = DayPickerProps

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn('p-3', className)}
      classNames={{
        months: 'rdp-months flex flex-col space-y-4 sm:flex-row sm:space-x-4 sm:space-y-0',
        month: 'rdp-month space-y-4',
        month_caption:
          'rdp-month_caption relative flex h-auto flex-col items-center gap-2 pt-1 sm:h-10 sm:flex-row sm:justify-center',
        caption_label: 'rdp-caption_label text-sm font-medium',
        button_previous: cn(
          'rdp-button_previous',
          buttonVariants({ variant: 'outline' }),
          'absolute left-1 top-1/2 h-7 w-7 -translate-y-1/2 bg-transparent p-0 opacity-50 hover:opacity-100'
        ),
        button_next: cn(
          'rdp-button_next',
          buttonVariants({ variant: 'outline' }),
          'absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2 bg-transparent p-0 opacity-50 hover:opacity-100'
        ),
        month_grid: 'rdp-month_grid w-full border-collapse space-y-1',
        weekdays: 'rdp-weekdays flex',
        weekday:
          'rdp-weekday w-9 rounded-md text-[0.8rem] font-normal text-muted-foreground',
        week: 'rdp-week mt-2 flex w-full',
        day: cn(
          buttonVariants({ variant: 'ghost' }),
          'h-9 w-9 p-0 font-normal'
        ),
        day_button: 'h-9 w-9 p-0 font-normal aria-selected:opacity-100',
        range_end: 'day-range-end',
        selected:
          'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground',
        today: 'bg-accent text-accent-foreground',
        outside:
          'day-outside text-muted-foreground opacity-50 aria-selected:bg-accent/50 aria-selected:text-muted-foreground aria-selected:opacity-30',
        disabled: 'text-muted-foreground opacity-50',
        range_middle:
          'aria-selected:bg-accent aria-selected:text-accent-foreground',
        hidden: 'invisible',
        dropdown_root: 'rdp-dropdown_root',
        dropdowns: 'rdp-dropdowns',
        dropdown: 'rdp-dropdown',
        months_dropdown: 'rdp-months_dropdown',
        years_dropdown: 'rdp-years_dropdown',
        chevron: 'fill-current',
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation }) => {
          const Icon = orientation === 'left' ? ChevronLeft : ChevronRight
          return <Icon className="h-4 w-4" />
        },
      }}
      {...props}
    />
  )
}

export { Calendar }
